import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  HiOutlineSquares2X2,
  HiOutlineClipboardDocumentList,
  HiOutlineWrenchScrewdriver,
  HiOutlineArrowsRightLeft,
  HiOutlineTableCells,
  HiOutlineCalendarDays,
  HiOutlineCircleStack,
  HiOutlineFire,
  HiOutlinePlay,
  HiOutlineShieldCheck,
  HiOutlineClock,
  HiOutlineArchiveBox,
  HiOutlineCog6Tooth,
  HiOutlineChevronDown
} from "react-icons/hi2";
import {
  MdKeyboardDoubleArrowLeft,
} from "react-icons/md";
import { RiArrowRightDoubleFill } from "react-icons/ri";
import Logo from "../../images/logo/Aluex-Logo.png";
import Logo1 from "../../images/logo/Bar.png";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const location = useLocation();
  const { pathname } = location;
  const sidebarRef = useRef(null);
  const navRef = useRef(null);
  const [openGroups, setOpenGroups] = useState({});
  const [activeFloatingMenu, setActiveFloatingMenu] = useState(null);
  const [hoveredInfo, setHoveredInfo] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (hoveredInfo) setHoveredInfo(null);
    };
    const navEl = navRef.current;
    if (navEl) navEl.addEventListener("scroll", handleScroll);
    return () => {
      if (navEl) navEl.removeEventListener("scroll", handleScroll);
    };
  }, [hoveredInfo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setActiveFloatingMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMenuClick = (key) => {
    if (sidebarCollapsed) {
      setActiveFloatingMenu(activeFloatingMenu === key ? null : key);
    } else {
      setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const menuGroups = [
    {
      key: "master",
      label: "Masters",
      icon: <HiOutlineSquares2X2 />,
      children: [{ label: "Masters", path: "/master" }],
    },
    {
      key: "die-purchase-order",
      label: "Die Purchase Order",
      icon: <HiOutlineClipboardDocumentList />,
      children: [
        { label: "Die Purchase Order", path: "/die-management/die-purchase-order" },
      ],
    },
    {
      key: "die-management",
      label: "Die Management",
      icon: <HiOutlineWrenchScrewdriver />,
      children: [{ label: "Die Master", path: "/die-management/die-master" }],
    },
    {
      key: "die-allocation",
      label: "Die Allocation",
      icon: <HiOutlineArrowsRightLeft />,
      children: [
        { label: "Die Allocation", path: "/die-management/die-allocation" },
      ],
    },
    {
      key: "sales-order",
      label: "Sales Order",
      icon: <HiOutlineTableCells />,
      children: [{ label: "Orders", path: "/sales-orders/order-master" }],
    },
    {
      key: "order-plan",
      label: "Order Plan",
      icon: <HiOutlineCalendarDays />,
      children: [{ label: "Order Plan View", path: "/order-plan-view" }],
    },
    {
      key: "raw-material",
      label: "Raw Material",
      icon: <HiOutlineCircleStack />,
      children: [
        { label: "Material Inward", path: "/material-inward" },
        { label: "All Stocks", path: "/all-stock" },
      ],
    },
    {
      key: "foundry",
      label: "Foundry",
      icon: <HiOutlineFire />,
      children: [{ label: "Foundry", path: "/foundry" }],
    },
    {
      key: "inProduction",
      label: "In Production",
      icon: <HiOutlinePlay />,
      children: [{ label: "In Prod", path: "/in-production" }],
    },
    {
      key: "qc",
      label: "QC",
      icon: <HiOutlineShieldCheck />,
      children: [{ label: "QC", path: "/qc" }],
    },
    {
      key: "aging",
      label: "Aging",
      icon: <HiOutlineClock />,
      children: [{ label: "Aging", path: "/aging" }],
    },
    {
      key: "packing",
      label: "Packing",
      icon: <HiOutlineArchiveBox />,
      children: [{ label: "Packing", path: "/packing" }],
    },
  ];

  return (
    <aside
      ref={sidebarRef}
      className={`fixed top-0 left-0 z-[999] h-screen flex flex-col bg-white border-r border-gray-200 transition-all duration-300 font-plus overflow-visible
    ${sidebarCollapsed ? "w-16" : "w-56"}
    ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
    >
      {/* Brand Header */}
      <div className="flex h-16 items-center justify-center border-b border-gray-100 shrink-0 bg-white">
        <NavLink to="/" className="px-4">
          <img src={sidebarCollapsed ? Logo1 : Logo} alt="Logo" className="h-10 w-auto object-contain transition-all hover:scale-105" />
        </NavLink>
      </div>

      {/* Navigation Registry */}
      <nav ref={navRef} className="flex-1 pt-4 px-0 space-y-1 overflow-y-auto no-scrollbar relative overflow-x-visible">
        {menuGroups.map((group) => {
          const hasSubmenu = group.children && group.children.length > 1;
          const isOpen = openGroups[group.key];
          const isFloatingOpen = activeFloatingMenu === group.key;

          const getMenuContent = (isActive = false) => (
            <div
              onMouseEnter={(e) => {
                if (sidebarCollapsed && !isFloatingOpen) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoveredInfo({ label: group.label, top: rect.top });
                }
              }}
              onMouseLeave={() => setHoveredInfo(null)}
              className={`flex items-center gap-4 relative w-full px-4 py-3 ${sidebarCollapsed ? 'justify-center' : ''}`}
            >
              <span className={`text-[18px] shrink-0 transition-all duration-300 ${isActive ? 'text-primary scale-110' : sidebarCollapsed ? 'text-gray-900 font-bold' : 'text-gray-400 group-hover:text-primary group-hover:scale-110'}`}>
                {group.icon}
              </span>
              {!sidebarCollapsed && (
                <span className={`text-[10px] font-black uppercase tracking-[2px] truncate transition-colors duration-300 ${isActive ? 'text-primary' : 'text-gray-600 group-hover:text-primary'}`}>
                  {group.label}
                </span>
              )}
            </div>
          );

          return (
            <div key={group.key} data-key={group.key} className="relative">
              {hasSubmenu ? (
                <button
                  onClick={() => handleMenuClick(group.key)}
                  className={`group relative w-full transition-all hover:bg-gray-50/80 ${isOpen && !sidebarCollapsed ? "bg-gray-50/80 border-b border-gray-100" : ""}`}
                >
                  {getMenuContent(isOpen && !sidebarCollapsed)}
                  {!sidebarCollapsed && (
                    <HiOutlineChevronDown size={12} className={`absolute right-5 top-1/2 -translate-y-1/2 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : "text-gray-300"}`} />
                  )}
                </button>
              ) : (
                <NavLink
                  to={group.children[0].path}
                  className={({ isActive }) => `group relative w-full flex items-center transition-all ${isActive ? "bg-primary/5 #3F80F7]" : "text-gray-500 hover:bg-gray-50"}`}
                >
                  {({ isActive }) => getMenuContent(isActive)}
                </NavLink>
              )}

              {!sidebarCollapsed && hasSubmenu && isOpen && (
                <ul className="bg-gray-50/30 py-2 border-b border-gray-100 space-y-1">
                  {group.children.map((item, idx) => (
                    <li key={idx} className="px-2">
                      <NavLink to={item.path} className={({ isActive }) => `flex items-center gap-4 pl-12 pr-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${isActive ? "text-primary bg-white " : "text-gray-400 hover:text-primary hover:bg-white"}`}>
                        <div className={`w-1 h-1 shrink-0 transition-all duration-300 ${pathname === item.path ? 'bg-primary scale-150' : 'bg-gray-200'}`}></div>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}

              {sidebarCollapsed && hasSubmenu && isFloatingOpen && (
                <div
                  style={{ top: `${sidebarRef.current?.querySelector(`[data-key="${group.key}"]`)?.getBoundingClientRect()?.top || 0}px` }}
                  className="fixed left-20 z-[9999] w-64 bg-white border border-gray-200  animate-in fade-in slide-in-from-left-2 duration-200"
                >
                  <div className="px-6 py-4 text-[10px] font-black text-white uppercase tracking-[3px] bg-gray-900 border-b-2 border-primary">
                    {group.label}
                  </div>
                  <ul className="py-2">
                    {group.children.map((item, idx) => {
                      const isItemActive = pathname === item.path;
                      return (
                        <li key={idx}>
                          <NavLink onClick={() => setActiveFloatingMenu(null)} to={item.path} className={`flex items-center gap-4 px-6 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${isItemActive ? 'text-primary bg-gray-50' : 'text-gray-500 hover:text-primary hover:bg-gray-50'}`}>
                            <div className={`w-1 h-1 shrink-0 transition-all duration-300 ${isItemActive ? 'bg-primary scale-150' : 'bg-gray-200'}`}></div>
                            {item.label}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Global Tooltip */}
      {sidebarCollapsed && hoveredInfo && (
        <div
          style={{ top: hoveredInfo.top + 8 }}
          className="fixed left-20 px-6 py-3 bg-gray-900 text-white text-[10px] font-black uppercase tracking-[3px] z-[99999]  border-l-[3px] border-primary animate-in fade-in slide-in-from-left-2 duration-200 pointer-events-none"
        >
          {hoveredInfo.label}
        </div>
      )}

      {/* Control Footer */}
      <div className="border-t border-gray-100 shrink-0 bg-white mt-auto">
        <button
          onClick={() => {
            setSidebarCollapsed(!sidebarCollapsed);
            setActiveFloatingMenu(null);
            localStorage.setItem("sidebar-expanded", !sidebarCollapsed);
          }}
          className="w-full flex items-center justify-center py-4 transition-all text-gray-400 hover:text-primary hover:bg-gray-50 group"
        >
          {sidebarCollapsed ? (
            <RiArrowRightDoubleFill size={22} className="group-hover:scale-125 transition-transform duration-300" />
          ) : (
            <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-[3px]">
              <MdKeyboardDoubleArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform duration-300" />
            </div>
          )}
        </button>
      </div>
    </aside>
  );
};


export default Sidebar;
